import { Empty, FoldablePanel, Note, Panel } from "./Panel";
import { ConsensusTakerReport } from "./ConsensusTakerReport";
import { StrategyLiveControls } from "./StrategyLiveControls";
import { StrategyRowActions } from "./StrategyRowActions";
import { useConsensusSignals, useConsensusSummary } from "../hooks/useConsensus";
import type { ConsensusCell, ConsensusSignal } from "../api/types";
import {
  bucketLabel,
  calibrationGist,
  cellLabel,
  chaseWord,
  driftWord,
  gridSize,
  lead,
  mainCells,
  meanPrice,
  outcomeWord,
  price,
  ratio,
  roiWord,
  rowTone,
  ruleWord,
  signalTitle,
  signedUsd,
  soloShare,
  soloTone,
  usd,
  verdict,
  zThreshold,
  zTone,
  zWord,
} from "../lib/consensus";

/**
 * Котировка когорты плюсовых тейкеров.
 *
 * # Зачем панель на экране
 *
 * Панель участников показывает предсказание группы — цену, по которой она
 * входила, — и на этом останавливается. Здесь та же котировка превращена в
 * ставку и сведена по исходу окна.
 *
 * Соседка перевеса, но вопрос ДРУГОЙ: тот считает, сколько денег группа занесла,
 * эта — по какой цене. Второе из первого не выводится: группа, купившая только
 * Down по 0.2, стоит в лонге Down и котирует Up 0.800.
 *
 * # Три сравнения, и все три обязательны
 *
 *   - против ЦЕНЫ ВХОДА — «есть ли кромка»: доля угаданного сама по себе не
 *     значит ничего;
 *   - против ФИЗИКИ — «есть ли она сверх замка»: один знак «цена против страйка»
 *     даёт 64–65 % на первой минуте окна и 91–92 % на четвёртой;
 *   - против САМОЙ КОТИРОВКИ — «калибрована ли группа». Это сравнение здесь
 *     новое и есть тот вопрос, ради которого журнал заведён.
 *
 * # Переключатель живого режима — здесь же, над сеткой
 *
 * Раньше в этом абзаце стояло «кнопок нет и не будет: у подсистемы нет порта
 * постановки». Порт появился (ADR-0027), и переключатель встал внутрь панели по
 * тому же доводу, что у трёх соседей: решение о режиме принимается по числам
 * замера — по тем самым, что стоят ниже, — и разведи их по разным экранам,
 * оператору пришлось бы помнить цифру, переходя по ссылке.
 *
 * Клетки в переключателе НЕТ, в отличие от почти решённого: у котировки её три
 * оси, и торгует ГЛАВНАЯ, назначенная до первых данных. Выдай мы выбор клетки —
 * торгующую можно было бы назначить задним числом по неделе данных, то есть
 * выбрать лучшую из тридцати шести гипотез. Ровно от этого главная клетка и
 * защищает, и звезда рядом с ней в матрице отчёта означает то же самое.
 */
export function ConsensusPanel({ hours, windowLen }: { hours: number; windowLen: number }) {
  const summary = useConsensusSummary(hours, windowLen);
  const cells = summary.data?.cells ?? [];
  const bins = summary.data?.calibration ?? [];
  const taker = summary.data?.taker_by_ask ?? [];
  const главные = mainCells(cells);

  return (
    <Panel
      title="Котировка когорты плюсовых тейкеров"
      subtitle={summary.data ? verdict(cells) : undefined}
    >
      {/* Отчёт тейкера — ПЕРВЫМ: это то, ради чего оператор открывает раздел с
          2026-09-03. Сетка 18 клеток × 2 роли, калибровка и мейкер остались, но
          ушли под фолд: они отвечают на вопросы замера, а не на вопрос
          «что вышло у тейкера по сигналу». */}
      <StrategyLiveControls id="consensus" />
      <ConsensusTakerReport hours={hours} />
      {!summary.data ? (
        <Empty>{summary.isError ? "сводка не загрузилась" : "загрузка…"}</Empty>
      ) : cells.length === 0 ? (
        <Empty>
          строк ещё не было: ставка рождается, только когда котировка группы
          перешагивает уверенность клетки, её ставка — ступень в долларах, и
          рынок даёт нужное число крупных согласных тейкерских филлов. Пороги
          строгие по замыслу — сколько ждать первой строки, само по себе ответ.
        </Empty>
      ) : (
        <FoldablePanel
          title="сетка клеток, калибровка и мейкер"
          subtitle="18 клеток × 2 роли, окно × корзина котировки, преследование L1"
          dense
        >
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-[var(--color-muted)]">
                <tr>
                  <th className="pb-2 font-normal">клетка</th>
                  <th className="pb-2 font-normal">минута окна</th>
                  <th className="pb-2 text-right font-normal">зачётных</th>
                  <th className="pb-2 text-right font-normal">погашено</th>
                  <th className="pb-2 text-right font-normal">сведено</th>
                  <th className="pb-2 text-right font-normal">угадано</th>
                  <th className="pb-2 text-right font-normal">против цены</th>
                  <th className="pb-2 text-right font-normal">против физики</th>
                  <th className="pb-2 text-right font-normal">против котировки</th>
                  <th className="pb-2 text-right font-normal">по направлению</th>
                  <th className="pb-2 text-right font-normal">извне группы</th>
                  <th className="pb-2 text-right font-normal">налито</th>
                  <th className="pb-2 text-right font-normal">стояло бы</th>
                  <th className="pb-2 text-right font-normal">доля кита</th>
                  <th className="pb-2 text-right font-normal">ставка</th>
                  <th className="pb-2 text-right font-normal">кошельков</th>
                  <th className="pb-2 text-right font-normal">тейкер ROI</th>
                </tr>
              </thead>
              <tbody>
                {cells.map((c, i) => (
                  <CellRow
                    key={`${c.consensus_bp}-${c.stake_step_usd}-${c.agree_n}-${c.bucket}`}
                    cell={c}
                    // Подпись клетки печатается ОДИН раз на группу: повторённая в
                    // каждой строке, она читалась бы как четыре разные клетки там,
                    // где это одна, разложенная по минутам окна.
                    first={i === 0 || !sameCell(cells[i - 1], c)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <Note label="калибровка: когда группа говорит 0.60 — сбывается в 60 % или в 80 %">
            <p className="mb-2 text-xs text-[var(--color-muted)]">
              Вопрос замера с 2026-09-03 — не «дешевле ли стакан котировки» (он
              дороже в четырёх строках из пяти, и это ответ), а <b>сколько на самом
              деле сбывается на каждом уровне уверенности группы</b>. Если на
              корзине 0.60 сбывается 0.80, положительное ожидание есть и на аске
              0.70. Единица — <b>окно × корзина</b>, первое попадание; строки сетки
              вложены, и одно окно лежало бы в корзине восемнадцать раз. Рядом
              стоят <b>аск и физика той же секунды</b>: доля без них читается как
              кромка, тогда как стакан к этому моменту обещал столько же.{" "}
              <b>{calibrationGist(bins)}.</b>
            </p>
            {bins.length === 0 ? (
              <Empty>сведённых окон ещё нет</Empty>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-left text-[var(--color-muted)]">
                    <tr>
                      <th className="pb-1 font-normal">котировка</th>
                      <th className="pb-1 text-right font-normal">окон</th>
                      <th className="pb-1 text-right font-normal">сбылось</th>
                      <th className="pb-1 text-right font-normal">против котировки</th>
                      <th className="pb-1 text-right font-normal">аск в ту секунду</th>
                      <th className="pb-1 text-right font-normal">против аска</th>
                      <th className="pb-1 text-right font-normal">физика</th>
                      <th className="pb-1 text-right font-normal">против физики</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bins.map((b) => (
                      <tr key={b.bin_bp} className="border-t border-[var(--color-border)]">
                        <td className="py-1 num">{b.bin}–{((b.bin_bp + 500) / 10000).toFixed(2)}</td>
                        <td className="py-1 text-right num">{b.windows}</td>
                        <td className="py-1 text-right num">
                          {b.won}/{b.windows} · {ratio(b.won, b.windows)}
                        </td>
                        <ZCell z={b.cons_z} n={b.windows} />
                        <td className="py-1 text-right num">{meanPrice(b.ask_sum, b.ask_known)}</td>
                        <ZCell z={b.ask_z} n={b.ask_known} />
                        <td className="py-1 text-right num">{meanPrice(b.phys_sum, b.phys_known)}</td>
                        <ZCell z={b.phys_z} n={b.phys_known} />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Note>

          <Note label="тейкер по сигналу: винрейт, счёт и ROI по цене входа">
            <p className="mb-2 text-xs text-[var(--color-muted)]">
              Тейкер <b>забирает живой стакан до потолка</b> (не до аска из снимка
              — так первые сутки терялась каждая четвёртая строка) на $25 и пишет
              эффективную цену входа. Здесь — <b>главная клетка</b> по корзинам этой
              цены: «стакан по 0.7» из вопроса «предсказание 0.6, реальный винрейт
              0.8 — заходить ли по 0.7» это корзина 0.70–0.80. ROI считается по
              строкам с посчитанным счётом и читается <b>только рядом с z</b>: на
              десятках строк знак суммы решают хвосты множителя выплаты.
            </p>
            {taker.length === 0 ? (
              <Empty>приценённых сведённых строк главной клетки ещё нет</Empty>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-left text-[var(--color-muted)]">
                    <tr>
                      <th className="pb-1 font-normal">цена входа</th>
                      <th className="pb-1 text-right font-normal">входов</th>
                      <th className="pb-1 text-right font-normal">угадано</th>
                      <th className="pb-1 text-right font-normal">безубыток</th>
                      <th className="pb-1 text-right font-normal">против цены</th>
                      <th className="pb-1 text-right font-normal">потрачено</th>
                      <th className="pb-1 text-right font-normal">счёт</th>
                      <th className="pb-1 text-right font-normal">ROI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {taker.map((b) => (
                      <tr key={b.bucket_bp} className="border-t border-[var(--color-border)]">
                        <td className="py-1 num">{b.bucket}–{((b.bucket_bp + 1000) / 10000).toFixed(2)}</td>
                        <td className="py-1 text-right num">{b.entries}</td>
                        <td className="py-1 text-right num">
                          {b.won}/{b.entries} · {ratio(b.won, b.entries)}
                        </td>
                        <td className="py-1 text-right num" title="средняя цена входа = доля, при которой счёт нулевой">
                          {meanPrice(b.price_sum, b.entries)}
                        </td>
                        <ZCell z={b.price_z} n={b.entries} />
                        <td className="py-1 text-right num">{usd(b.cost)}</td>
                        <td className="py-1 text-right num">{signedUsd(b.pnl)}</td>
                        <td className="py-1 text-right num" style={{ color: zTone(b.price_z) }}>
                          {roiWord(b.roi)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Note>

          <Note label="преследование L1: сколько стоит гнаться за бидом">
            <p className="text-xs text-[var(--color-muted)]">
              Средний <b>снос на наливе</b> (аск минус наша цена в тот момент) по
              главной клетке — <b>{driftWord(главные)}</b>. Это и есть главное
              число мейкерской половины: <b>чем ближе к нулю, тем меньше смысла в
              мейкере</b> — преследование подтянуло нашу цену к аску, и вся
              экономия свелась к комиссии. Вывод шире этой подсистемы: он касается
              всех мейкерских гипотез проекта. Колонка «стояло бы» показывает ту же
              строку при ФИКСИРОВАННОЙ цене — обе гипотезы считаются из одной
              записи, без второго журнала.
            </p>
          </Note>

          <Note label="как читать: вложенность клеток, три сравнения и два прочтения">
            <b>КЛЕТКИ ВЛОЖЕНЫ — столбец складывать нельзя.</b> Строка с котировкой
            0.72 попала в каждую клетку до 0.70 включительно, и сумма по столбцу
            «уверенность» посчитает её трижды. То же правило и та же причина, что
            у сетки порогов расхождения.{" "}
            <b>Судить надо ГЛАВНУЮ клетку</b> ({главные.length > 0 ? cellLabel(главные[0]!) : "0.65 / $1500 / N=2"}
            ), назначенную до первых данных: клеток {gridSize(cells)}, ролей две,
            гипотез {2 * gridSize(cells)}, и лучшая из них выглядит кромкой ПО
            ПОСТРОЕНИЮ — порог значимости здесь |z| ≈{" "}
            {zThreshold(gridSize(cells)).toFixed(1)}, а не 2.{" "}
            <b>Три сравнения обязательны.</b> Против цены — «есть ли кромка»;
            против физики — «есть ли она сверх замка»;{" "}
            <b>против котировки — «калибрована ли сама группа»</b>, то есть
            сбывается ли то, что она обещала своими деньгами. Третье специфично для
            этого журнала.{" "}
            <b>«По направлению» — второе прочтение подтверждения.</b> Триггер
            читает крупные филлы ПО ОСИ: покупка Down по 0.35 засчитывается как
            «0.65 в сторону Up», но такой кошелёк ставит НА DOWN. Одно из прочтений
            меряет пустоту, и колонка отвечает какое — числом, а не рассуждением.{" "}
            <b>«Извне группы»</b> — строки, где подтверждение дали не её же члены:
            иначе второе условие пересказывает первое.{" "}
            <b>«Доля кита» — канарейка:</b> устойчиво больше половины означает, что
            меряется один кошелёк, а не когорта. <b>«Погашено» не прячется</b> — без
            него нельзя ответить, сколько потока съели пороги. Ордеров подсистема не
            ставит и поставить не может: порта постановки у неё нет.
          </Note>
        </FoldablePanel>
      )}
    </Panel>
  );
}

/** Одна ли это клетка сетки. Ведро минуты окна в сравнение не входит. */
function sameCell(a: ConsensusCell | undefined, b: ConsensusCell): boolean {
  return (
    a !== undefined &&
    a.consensus_bp === b.consensus_bp &&
    a.stake_step_usd === b.stake_step_usd &&
    a.agree_n === b.agree_n
  );
}

function CellRow({ cell, first }: { cell: ConsensusCell; first: boolean }) {
  return (
    <tr className="border-t border-[var(--color-border)]">
      <td className="py-1">
        {first ? (
          <span style={{ color: cell.main_cell ? "var(--color-fg)" : undefined }}>
            {cellLabel(cell)}
            {cell.main_cell && (
              <span className="ml-1 text-[var(--color-accent)]" title="главная клетка, назначенная до первых данных">
                ★
              </span>
            )}
          </span>
        ) : null}
      </td>
      <td className="py-1">{bucketLabel(cell.bucket)}</td>
      <td className="py-1 text-right num">{cell.signals}</td>
      <td className="py-1 text-right num text-[var(--color-muted)]">{cell.suppressed}</td>
      <td className="py-1 text-right num">{cell.settled}</td>
      <td className="py-1 text-right num">{ratio(cell.won, cell.settled)}</td>
      <ZCell z={cell.price_z} n={cell.priced} />
      <ZCell z={cell.phys_z} n={cell.phys_known} />
      <ZCell z={cell.cons_z} n={cell.cons_known} />
      <ZCell z={cell.side_z} n={cell.by_side} />
      <ZCell z={cell.outside_z} n={cell.outside} />
      <td className="py-1 text-right num" title="налив преследующей заявки против наблюдавшихся строк">
        {cell.observed > 0 ? `${cell.filled}/${cell.observed}` : "—"}
      </td>
      <td
        className="py-1 text-right num"
        title="налив ФИКСИРОВАННОЙ заявки по цене постановки — вторая гипотеза из той же строки"
      >
        {cell.observed > 0 ? `${cell.standing}/${cell.observed}` : "—"}
      </td>
      <td className="py-1 text-right num" style={{ color: soloTone(cell.top_share_median) }}>
        {soloShare(cell.top_share_median)}
      </td>
      <td className="py-1 text-right num">{usd(cell.stake_median)}</td>
      <td className="py-1 text-right num">{cell.wallets_median}</td>
      <td
        className="py-1 text-right num"
        title={`счёт тейкера ${signedUsd(cell.taker_pnl)} на потраченные ${usd(cell.taker_cost)}`}
      >
        {roiWord(cell.taker_roi)}
      </td>
    </tr>
  );
}

/**
 * Ячейка z-оценки со знаменателем в подсказке.
 *
 * Знаменатель обязателен: ноль в разности бывает двух родов, и без него пустое
 * измерение выглядит как измеренный ноль.
 */
function ZCell({ z, n }: { z: number | undefined; n: number }) {
  return (
    <td
      className="py-1 text-right num"
      style={{ color: zTone(z) }}
      title={n > 0 ? `по ${n} строкам` : "считать не из чего"}
    >
      {zWord(z)}
    </td>
  );
}

/**
 * Лента теневых ставок: по строке на решение, вместе с погашенными.
 *
 * Отдельным компонентом от сводки, чтобы страница могла поставить ленты НИЖЕ
 * обеих сводок: сравнивают сводки друг с другом, а не со своей лентой.
 *
 * Обёрнута в FoldablePanel: свёрнутое тело НЕ МОНТИРУЕТСЯ, и её хук перестаёт
 * опрашивать сервер раз в пятнадцать секунд просто за то, что панель на экране.
 */
export function ConsensusFeed({ hours }: { hours: number }) {
  return (
    <FoldablePanel
      title="Лента котировки когорты"
      subtitle="по строке на клетку, вместе с погашенными правилом"
    >
      <ConsensusFeedBody hours={hours} />
    </FoldablePanel>
  );
}

/** Тело ленты — отдельный компонент: свёрнутое не монтируется и опроса не заводит. */
export function ConsensusFeedBody({ hours }: { hours: number }) {
  const feed = useConsensusSignals(hours);
  const signals = feed.data?.signals ?? [];

  if (!feed.data) {
    return <Empty>{feed.isError ? "лента не загрузилась" : "загрузка…"}</Empty>;
  }
  if (signals.length === 0) {
    return <Empty>строк за окно не было</Empty>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="text-left text-[var(--color-muted)]">
          <tr>
            <th className="pb-2 font-normal">рынок</th>
            <th className="pb-2 font-normal">клетка</th>
            <th className="pb-2 text-right font-normal">котировка</th>
            <th className="pb-2 font-normal">сторона</th>
            <th className="pb-2 text-right font-normal">ставка</th>
            <th className="pb-2 text-right font-normal">крупных</th>
            <th className="pb-2 text-right font-normal">аск</th>
            <th className="pb-2 text-right font-normal">фора</th>
            <th className="pb-2 font-normal">преследование</th>
            <th className="pb-2 font-normal">итог</th>
            <th className="pb-2 font-normal">правило</th>
            <th className="pb-2 font-normal" />
          </tr>
        </thead>
        <tbody>
          {signals.map((s) => (
            <SignalRow key={s.id} signal={s} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SignalRow({ signal: s }: { signal: ConsensusSignal }) {
  return (
    <tr className="border-t border-[var(--color-border)]" style={{ color: rowTone(s) }}>
      <td className="py-1" title={signalTitle(s)}>
        {s.asset}
      </td>
      <td className="py-1">
        {cellLabel(s)}
        {s.main_cell && <span className="ml-1 text-[var(--color-accent)]">★</span>}
      </td>
      <td className="py-1 text-right num">{price(s.consensus_price)}</td>
      {/* Сторона предсказана ЦЕНОЙ и законно указывает против нетто-позиции
          группы. На экране это невидимо, поэтому объяснение — в подсказке. */}
      <td className="py-1" title="сторона предсказана ценой, а не нетто-позицией группы">
        {s.outcome_name || "—"}
      </td>
      <td className="py-1 text-right num">{usd(s.stake_usd)}</td>
      <td
        className="py-1 text-right num"
        title={`по оси ${s.big_axis_500}, из них по направлению ${s.big_side_500}, из группы ${s.big_group_500}`}
      >
        {s.big_axis_500}/{s.big_side_500}
      </td>
      <td className="py-1 text-right num">{price(s.taker_price)}</td>
      <td className="py-1 text-right num">{lead(s.lead_ms)}</td>
      <td className="py-1">{chaseWord(s)}</td>
      <td className="py-1">{outcomeWord(s)}</td>
      <td className="py-1 text-[var(--color-muted)]">{ruleWord(s.rule)}</td>
      {/* Кнопка — только у ГЛАВНОЙ клетки и только у чистой строки: по остальным
          трейдер отвергнет нажатие правилом (`cell` и правило измерения), и
          кнопка обещала бы действие, которого не будет. По одному окну строк до
          восемнадцати, и звезда в соседней колонке помечает ту самую. */}
      <td className="py-1 text-right">
        {s.main_cell && !s.rule && (
          <StrategyRowActions
            strategy="consensus"
            signalID={s.id}
            windowEnd={s.window_end}
          />
        )}
      </td>
    </tr>
  );
}
